import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useField } from 'react-final-form';
import { useHistory } from 'react-router';
import { isAfter, isBefore, max, min, parse } from 'date-fns';
import spacetime from 'spacetime';
import debounce from 'lodash.debounce';
import cn from 'classnames';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import {
  KNO_ORGANIZATION_BLOCK_ID,
  PROSECUTOR_ORGANIZATION_BLOCK_ID,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import {
  knmInfoGetExcludeDates,
  knmInfoGetWorkDaysCountBeforeStart,
  knmSetPmIsUnableToRefuseFormValue,
  knmSetSupervisionType,
} from 'KnmPackage/MainModule/actions/knmActions';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { PmReasons } from 'KnmPmPackage/PmReasonsModule/components/PmReasons';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { KNM_CREATE_ERROR } from 'CorePackage/MainModule/constants/notices';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IErknmInfoExcludeDatesResponse } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesResponse';
import { fetchIsUnableToRefuseFlagPm } from 'APIPackage/DictionariesModule/services/dictionariesApi';
import { dictionariesProfKindsItemGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';

import styles from './CreatePmModal.module.scss';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmErknmSupervisionTypesSelectOptions,
  getKnmOnSaveButtonClick,
  getKnmFormRef,
  getKnmIsWatchOnly,
  getKnmErknmProfKindsSelectOptions,
  getKnmExcludeDates,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { getDictionariesKnoOrganizationsSelectOptions } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { getAccountOrganizationsWithDistrictData } from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { CheckNotificationModal } from '../../../CorePackage/MainModule/components/CheckNotificationModal';
import { CheckNotificationTypesEnum } from '../../../CorePackage/MainModule/enums/CheckNotificationTypesEnum';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { formatDate, parseDate } from '../../../CorePackage/MainModule/common/formatDate';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { INotification } from '../../../CorePackage/NotificationModule/interfaces/INotification';
import {
  getUserRoleIsOmbudsman,
  getUserRoleIsProsecutorOrSuperProsecutor,
} from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { ROUTE_KNM_LIST } from '../../../CorePackage/MainModule/routes/routes';
import { Notification } from '../../../CorePackage/NotificationModule/components/Notification';
import { PM_TYPE_BLOCK_ID, START_DATE_BLOCK_ID, SUPERVISION_TYPE_BLOCK_ID } from '../constants/knmPmFieldsElementsIds';
import { IKnmPmForm } from '../interfaces/IKnmPmForm';
import { IKnmPmFormReason } from '../interfaces/IKnmPmFormReason';
import { restorePmInitialValues } from './restorePmInitialValues';

const cloneReason = (reason: IKnmPmFormReason): IKnmPmFormReason => ({
  ...reason,
  type: reason.type ? { ...reason.type } : undefined,
  reason: reason.reason ? { ...reason.reason } : undefined,
  riskIndicators: reason.riskIndicators ? [...reason.riskIndicators] : undefined,
  links: reason.links ? [...reason.links] : undefined,
});

const cloneReasons = (reasons: IKnmPmFormReason[]): IKnmPmFormReason[] => reasons.map(cloneReason);

interface ICreatePmModalProps {
  visible: boolean;
  values: IKnmPmForm;
  onClose: () => void;
  onCloseSuccess: () => void;
  knoOrganizationPm: IDictionaryKnoOrganizationSelectOption | undefined;
  reasonsPmValue: IKnmPmFormReason[] | undefined;
  startDate: string | undefined;
  stopDate: string | undefined;
  pmTypesSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
}

interface ICreatePmModalStoreProps {
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isOmbudsman: boolean;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  onSaveButtonClick: (() => void) | undefined;
  userKnoOrganisations: IUserOrganisation[];

  pmTypesSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  excludeDates: IErknmInfoExcludeDatesResponse | undefined;
}
const emptyReason: IKnmPmFormReason = {
  dateCreate: undefined,
  fullName: undefined,
  knmIdLink: undefined,
  links: undefined,
  numGuid: undefined,
  order: undefined,
  planKnm: undefined,
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  inspectionResults: undefined,
  ervkUuid: undefined,
  reason: undefined,
  listId: uuidv4(),
};

export const CreatePmModalWithoutStore = ({
  formRef,
  visible,
  values,
  onClose,
  onCloseSuccess,
  isWatchOnly,
  knoOrganizationsSelectOptions,
  supervisionTypesSelectOptions,
  onSaveButtonClick,
  reasonsPmValue,
  userKnoOrganisations,
  startDate,
  stopDate,
  isProsecutorOrSuperProsecutor,
  isOmbudsman,
  pmTypesSelectOptions,
  knoOrganizationPm,
  excludeDates,
}: ICreatePmModalProps & ICreatePmModalStoreProps) => {
  const push = formRef?.current.mutators.push;
  const history = useHistory();
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const hasRestoredValues = useRef(false);
  const restoredFieldsRef = useRef<Set<string>>(new Set());
  const isRestoringRef = useRef(false);
  const restoredReasonsPmRef = useRef<IKnmPmFormReason[] | undefined>(undefined);
  const [restoredReasonsPm, setRestoredReasonsPm] = useState<IKnmPmFormReason[] | undefined>(undefined);

  const markFieldAsRestored = useCallback((fieldName: string) => {
    restoredFieldsRef.current.add(fieldName);
  }, []);

  const restoreInitialValues = useCallback(() => {
    const saved = sessionStorage.getItem('initialValuesPm');
    const savedPmErknm = sessionStorage.getItem('initialValuesPmErknm');

    if (saved && savedPmErknm && formRef?.current && !hasRestoredValues.current) {
      try {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const savedValues = JSON.parse(saved);

        const savedValuesPmErknm = JSON.parse(savedPmErknm) as Record<string, any>;

        const { hasRestored } = restorePmInitialValues({
          savedValues,
          savedValuesPmErknm,
          formRef,
          values,
          knoOrganizationPm,
          startDate,
          stopDate,
          markFieldAsRestored,
          cloneReasons,
          setRestoredReasonsPm,
          restoredReasonsPmRef,
          isRestoringRef,
        });

        if (hasRestored) {
          hasRestoredValues.current = true;
          sessionStorage.removeItem('initialValuesPmErknm');
        }
      } catch (e) {
        console.error('Error restoring initial values:', e);
        sessionStorage.removeItem('initialValuesPm');
        sessionStorage.removeItem('initialValuesPmErknm');
        sessionStorage.removeItem('initialInvalidFields');
      }
    }
  }, [formRef, knoOrganizationPm, markFieldAsRestored, startDate, stopDate, values, cloneReasons]);

  useEffect(() => {
    if (visible && formRef?.current && !hasRestoredValues.current) {
      const timer = setTimeout(() => {
        restoreInitialValues();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [visible, formRef, restoreInitialValues]);

  useEffect(() => {
    if (!visible) {
      hasRestoredValues.current = false;
      restoredFieldsRef.current.clear();
      restoredReasonsPmRef.current = undefined;
      setRestoredReasonsPm(undefined);
    }
  }, [visible]);

  if (isOmbudsman || isProsecutorOrSuperProsecutor) {
    history.push(ROUTE_KNM_LIST);
  }

  const [isShowNotice, setIsShowNotice] = useState(false);
  const [errorNotice, setErrorNotice] = useState<INotification | undefined>(undefined);
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState<boolean>(false);

  const hasInitializedReasons = useRef(false);

  useEffect(() => {
    if (!isWatchOnly && push && !hasInitializedReasons.current) {
      const currentValues = formRef?.current?.getState().values;
      const hasReasonsPm =
        currentValues?.reasonsPm && Array.isArray(currentValues.reasonsPm) && currentValues.reasonsPm.length > 0;

      if (!reasonsPmValue || reasonsPmValue.length < 1) {
        if (!hasReasonsPm) {
          push('reasonsPm', emptyReason);
        }
        hasInitializedReasons.current = true;
      }
    }
  }, [reasonsPmValue, push, isWatchOnly, formRef]);

  useEffect(() => {
    if (visible && hasRestoredValues.current && knoOrganizationPm && formRef?.current) {
      const currentValues = formRef.current.getState().values;

      if (!currentValues?.knoOrganizationPm) {
        const timer = setTimeout(() => {
          formRef.current?.change('knoOrganizationPm', knoOrganizationPm);
        }, 150);

        return () => clearTimeout(timer);
      }
    }
  }, [visible, knoOrganizationPm, formRef]);

  useEffect(() => {
    const hasSessionStorageData = !!sessionStorage.getItem('initialValuesPm');

    if (
      visible &&
      !hasRestoredValues.current &&
      !hasSessionStorageData &&
      userKnoOrganisations &&
      knoOrganizationsSelectOptions &&
      formRef?.current
    ) {
      const currentValues = formRef.current.getState().values;

      if (!currentValues?.knoOrganizationPm) {
        if (knoOrganizationPm) {
          formRef.current.change('knoOrganizationPm', knoOrganizationPm);
        } else {
          const byDefaultKno = userKnoOrganisations.find(i => i.byDefault);

          if (byDefaultKno) {
            const knoOrg = knoOrganizationsSelectOptions.find(i => i.idStr?.toString() === byDefaultKno.id);

            if (knoOrg) {
              formRef.current.change('knoOrganizationPm', knoOrg);
            }
          }
        }
      }
    }
  }, [visible, formRef, userKnoOrganisations, knoOrganizationsSelectOptions, knoOrganizationPm]);

  const isFieldRestored = useCallback((fieldName: string) => restoredFieldsRef.current.has(fieldName), []);

  useEffect(() => {
    if (restoredReasonsPm && formRef?.current) {
      isRestoringRef.current = true;
      formRef.current.change('reasonsPm', cloneReasons(restoredReasonsPm));
      isRestoringRef.current = false;
    }
  }, [restoredReasonsPm, formRef]);

  const pmReasonsKey = useMemo(() => {
    const source = restoredReasonsPm ?? values.reasonsPm;

    if (!source?.length) {
      return 'pm-reasons-empty';
    }

    return `pm-reasons-${source.map(reason => reason?.listId || '').join('-')}`;
  }, [restoredReasonsPm, values.reasonsPm]);

  const getPmTypeOptions = () => {
    const { knoOrganizationPm: knoOrganizationPmValue, supervisionTypePm: supervisionTypePmValue, reasonsPm } =
      formRef?.current.getState().values ?? {};

    if (knoOrganizationPmValue && supervisionTypePmValue) {
      const conditionUuid = reasonsPm?.[0]?.reason?.ervkUuid ?? reasonsPm?.[0]?.ervkUuid;

      dispatch(
        dictionariesProfKindsItemGet(
          {
            knoVersionId: String(knoOrganizationPmValue?.value.dictVersionId),
            supervisionVersionId: String(supervisionTypePmValue?.value.dictVersionId),
          },
          conditionUuid ? String(conditionUuid) : undefined,
        ),
      );
    }
  };

  const validateStopDate = (stopDateValue: string | undefined, allValues: IKnmPmForm) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { startDate } = allValues;

    if (!stopDateValue || !startDate) {
      return undefined;
    }

    const parsedStopDate = parse(stopDateValue, SYSTEM_DATE_FORMAT_FNS, new Date());
    const parsedStartDate = parse(startDate, SYSTEM_DATE_FORMAT_FNS, new Date());

    if (isBefore(parsedStopDate, parsedStartDate)) {
      return 'Дата окончания не может быть раньше даты начала';
    }

    return undefined;
  };

  const handleKnoOrganizationChange = useCallback(() => {
    formRef?.current.change('kindControl');
    formRef?.current.change('kindKnm');
    formRef?.current.change('reasonsPm', [emptyReason]);
    formRef?.current.change('approveRequired');
  }, [formRef]);

  const handleCloseNotification = useCallback(() => {
    setIsShowNotice(false);
  }, []);

  const pmTypeCode = values.pmType?.code;
  const isTypeOP = pmTypeCode === ProfKindCodesEnum.OP;
  const isTypePP = pmTypeCode === ProfKindCodesEnum.PP;
  const startDateLabel = isTypeOP ? 'Дата объявления предостережения' : 'Дата начала мероприятия';
  const stopDateLabel = isTypePP ? 'Дата окончания мероприятия' : 'Дата окончания';

  const onClickSaveButton = useCallback(() => {
    if (!formRef?.current || isWatchOnly) return;

    formRef.current.submit();

    const state = formRef.current.getState();
    const { errors } = state;

    if (
      (Array.isArray(errors?.reasonsPm) && errors?.reasonsPm.some(e => e)) ||
      errors?.stopDate ||
      errors?.startDate ||
      errors?.pmType
    ) {
      const error: INotification = {
        id: uuidv4(),
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.SAVE_KNM,
        renderContent: KNM_CREATE_ERROR,
      };

      setErrorNotice(error);
      setIsShowNotice(true);

      return;
    }

    setErrorNotice(undefined);
    setIsShowNotice(false);
    if (onSaveButtonClick) {
      onSaveButtonClick();
    }
    onCloseSuccess();
  }, [formRef, isWatchOnly, onCloseSuccess]);

  const onClickButtonApproveNotification = useCallback(() => {
    setIsNotificationModalVisible(false);
    onSaveButtonClick?.();
  }, [onSaveButtonClick]);

  const handleClearReason = useCallback(() => {
    if (isRestoringRef.current) {
      return;
    }

    const reasonsPm = formRef?.current?.getState().values?.reasonsPm;

    if (Array.isArray(reasonsPm) && reasonsPm.length > 0) {
      reasonsPm.forEach((_, index) => {
        formRef?.current.change(`reasonsPm[${index}].ervkUuid` as keyof IKnmPmForm);
        formRef?.current.change(`reasonsPm[${index}].type` as keyof IKnmPmForm);
      });
      formRef?.current.change('isUnableToRefuse');
    }
  }, [formRef]);

  const handleSupervisionTypeChange = useCallback(
    (value: IDictionarySupervisionTypeSelectOption | undefined) => {
      if (value) {
        formRef?.current.change('supervisionTypePm', {
          ...value,
          label: value.selectedLabel || value.label,
        });
        dispatch(knmSetSupervisionType());
      } else {
        formRef?.current.change('supervisionTypePm');
      }

      formRef?.current.change('pmType');
      formRef?.current.change('isFiz');
      handleClearReason();
    },
    [formRef, dispatch],
  );

  const handlePmTypeChange = useCallback(() => {
    formRef?.current.change('pmForm');
    formRef?.current.change('officialFullName');
    formRef?.current.change('signers');

    const currentValues = formRef?.current.getState().values;

    if (currentValues?.pmType?.code === ProfKindCodesEnum.OP) {
      formRef?.current.change('stopDate');
    }

    if (currentValues?.pmType?.code === ProfKindCodesEnum.PP && currentValues?.reasonsPm?.[0]?.ervkUuid) {
      getIsUnableToRefuse(currentValues.reasonsPm[0].ervkUuid);
    }
  }, [formRef]);

  const getIsUnableToRefuse = async (reasonErvkUuid: string, orderId?: string, orderVersionId?: string) => {
    const isUnableToRefuse = await fetchIsUnableToRefuseFlagPm(reasonErvkUuid, orderId, orderVersionId);

    dispatch(knmSetPmIsUnableToRefuseFormValue(isUnableToRefuse));
    formRef?.current.change('isUnableToRefuse', isUnableToRefuse);
  };

  const onClearReason = useCallback(() => {
    formRef?.current.change('pmType');
    handleClearReason();
  }, [formRef]);

  const handleGetWorkDaysCountBeforeStart = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (startDate: string) => {
      const formattedDate = formatDate({ date: startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS });
      const formDate = spacetime(formattedDate || '');
      const currentDate = spacetime();

      if (formDate.isAfter(currentDate) || formDate.isSame(currentDate, 'date')) {
        dispatch(knmInfoGetWorkDaysCountBeforeStart(formattedDate));
      }
    },
    [dispatch],
  );

  const debouncedHandleGetWorkDaysCountBeforeStart = useCallback(debounce(handleGetWorkDaysCountBeforeStart, 500), [
    handleGetWorkDaysCountBeforeStart,
  ]);

  const startDateField = useField('startDate', { subscription: { value: true } });
  const startDateValue = startDateField.input.value;

  useEffect(() => {
    if (startDateValue && stopDate) {
      const start = parseDate({ date: startDateValue, parseFormat: SYSTEM_DATE_FORMAT_FNS });
      const stop = parseDate({ date: stopDate, parseFormat: SYSTEM_DATE_FORMAT_FNS });

      if (start && stop && isAfter(start, stop)) {
        formRef?.current.change('stopDate', undefined);
      }
    }
  }, [startDateValue, stopDate, formRef]);

  const typePmField = useField('pmType' as keyof IKnmForm, { subscription: { value: true } });
  const [knmStartMinDate, setKnmStartMinDate] = useState<Date | undefined>();
  const [pmStartExcludeDates, setPmStartExcludeDates] = useState<Date[] | undefined>();
  const [pmEndExcludeDates, setPmEndExcludeDates] = useState<Date[] | undefined>();
  const [pmEndMinDate, setPmEndMinDate] = useState<Date | undefined>();

  const updateExcludeDatesWithStartDate = useCallback(() => {
    const firstReason = formRef?.current?.getState().values.reasonsPm?.[0];

    dispatch(
      knmInfoGetExcludeDates({
        approveRequiredCode: '',
        kindCode: '',
        typeCode: typePmField.input.value?.code ?? '',
        reasonTypeId: firstReason?.type?.id,
        reasonName: firstReason?.type?.name,
        selectedStartDate: undefined,
        isNewKnm: false,
      }),
    );
  }, [dispatch, typePmField]);
  const startDatePickerField = () => {
    updateExcludeDatesWithStartDate();
  };

  useEffect(() => {
    if (!startDate) {
      setKnmStartMinDate(new Date());

      return;
    }
    const parsedStartDate = parse(startDate, DATE_FORMAT_LONG_FNS, new Date());

    if (excludeDates) {
      setKnmStartMinDate(min([new Date(excludeDates.startDate.minDate), parsedStartDate]));
    } else {
      setKnmStartMinDate(parsedStartDate);
    }
  }, [excludeDates, startDate]);

  useEffect(() => {
    if (excludeDates) {
      setKnmStartMinDate(new Date(excludeDates.startDate.minDate));
      const parsedStartDate = parseDate({
        date: startDate || new Date(),
        parseFormat: DATE_FORMAT_LONG_FNS,
      });

      if (parsedStartDate) {
        setPmEndMinDate(max([new Date(excludeDates.stopDate.minDate), parsedStartDate]));
      } else {
        setPmEndMinDate(new Date(excludeDates.stopDate.minDate));
      }

      setPmStartExcludeDates(excludeDates.startDate.excludeDates.map(date => new Date(date)));
      setPmEndExcludeDates(excludeDates.stopDate.excludeDates.map(date => new Date(date)));
    }
  }, [excludeDates, formRef, startDate]);

  const pmTypeField = useField('pmType', { subscription: { value: true } });
  const pmTypeValue = pmTypeField.input.value;

  const onClearNoMatchingReason = () => {
    if (isRestoringRef.current || pmTypeValue) {
      return;
    }

    onClearReason();
  };

  return (
    <Modal
      modalBodyClassName={styles.ModalBody}
      modalClassName={styles.Modal}
      visible={visible}
      onClose={onClose}
      isOutsideClick={false}
    >
      <header className={styles.Header}>
        <Row margin="none">
          <div className={styles.HeaderTitle}>Создание профилактического мероприятия</div>
          {isShowNotice && errorNotice && (
            <div className={styles.NotificationWrapper}>
              <Notification notice={errorNotice} close={handleCloseNotification} />
            </div>
          )}
        </Row>
      </header>
      <ModalBody size="content" border="bottom" overflow="scroll">
        <Row notSpace notWrap>
          <div className={styles.InfoBody}>
            <div className={styles.TooltipIcon}></div>
            <div>Изменение отраженных ниже полей будет заблокировано после сохранения формы</div>
          </div>
        </Row>

        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Контрольный (надзорный) орган`,
              field: `knoOrganizationPm`,
              isPm: true,
              isDecision: false,
              isKnm: false,
            })}
            placementLabel="left"
            id={KNO_ORGANIZATION_BLOCK_ID}
            className={styles.label}
          >
            <SelectInputField
              id="knoOrganizationPm"
              name="knoOrganizationPm"
              options={knoOrganizationsSelectOptions}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={true}
              className={styles.width_82}
              validate={makeRequired('Поле не может быть пустым')}
              onChange={handleKnoOrganizationChange}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Орган прокуратуры`,
              field: `prosecutorOrganizationErknm`,
              isPm: true,
              isDecision: false,
              isKnm: false,
            })}
            placementLabel="left"
            id={PROSECUTOR_ORGANIZATION_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="prosecutorOrganizationErknm"
              name="prosecutorOrganizationErknm"
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={true}
              className={styles.width_82}
            />
          </FieldBlock>
        </Row>

        <div className={cn(styles.input_container, styles.marginBottom)} id={SUPERVISION_TYPE_BLOCK_ID}>
          <div className={styles.label}>
            <label htmlFor="supervisionTypePm">
              Вид контроля (надзора)<span className={styles.redStar}>*</span>
            </label>
          </div>
          <SelectInputField
            id="supervisionTypePm"
            name="supervisionTypePm"
            options={supervisionTypesSelectOptions}
            isSearchable
            disabled={isWatchOnly || isFieldRestored('supervisionTypePm')}
            size="medium"
            onChange={handleSupervisionTypeChange}
            className={styles.width_75}
          />
          <div className={styles.tooltipContainer}>
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующем поле:
                  <ul>
                    <p>Контрольный (надзорный) орган</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          </div>
        </div>

        {isTypeOP ? (
          <div className={styles.input_container} id={START_DATE_BLOCK_ID}>
            <div className={styles.label}>
              <label htmlFor="startDate">
                {startDateLabel}
                <span className={styles.redStar}>*</span>
              </label>
            </div>
            <DatePickerField
              className={styles.width_75}
              id="startDate"
              name="startDate"
              disabled={isWatchOnly}
              onChange={date => {
                startDatePickerField();
                onClearNoMatchingReason();
                if (date) debouncedHandleGetWorkDaysCountBeforeStart(date.toString());
              }}
              minDate={knmStartMinDate}
              excludeDates={pmStartExcludeDates}
              opensOnTop
            />
          </div>
        ) : (
          <div className={styles.dateRangeContainer}>
            <div className={styles.dateFieldWrapper}>
              <label htmlFor="startDate" className={styles.dateLabel}>
                {startDateLabel}
                <span className={styles.redStar}>*</span>
              </label>
              <DatePickerField
                className={styles.datePicker}
                id="startDate"
                name="startDate"
                disabled={isWatchOnly}
                minDate={knmStartMinDate}
                excludeDates={pmStartExcludeDates}
                onChange={date => {
                  startDatePickerField();
                  onClearNoMatchingReason();
                  if (date) debouncedHandleGetWorkDaysCountBeforeStart(date.toString());
                }}
                opensOnTop
              />
            </div>
            <div className={styles.dateFieldWrapper}>
              <label htmlFor="stopDate" className={styles.dateLabel}>
                {stopDateLabel}
                <span className={styles.redStar}>*</span>
              </label>
              <DatePickerField
                className={styles.datePicker}
                id="stopDate"
                name="stopDate"
                disabled={isWatchOnly}
                onChange={() => onClearNoMatchingReason()}
                validate={validateStopDate}
                excludeDates={startDate ? undefined : pmEndExcludeDates}
                minDate={pmEndMinDate}
                opensOnTop
              />
            </div>
          </div>
        )}

        <PmReasons
          key={pmReasonsKey}
          className={knmSharedStyles.Section}
          watchOnly={isWatchOnly}
          pmTypeCode={pmTypeCode}
          reasonsPm={values.reasonsPm}
          autoAddReason={true}
          initialReasonsPm={restoredReasonsPm ?? values.reasonsPm}
        />

        <div className={cn(styles.input_container, styles.marginTop, styles.marginBottom)} id={PM_TYPE_BLOCK_ID}>
          <div className={styles.label}>
            <label htmlFor="pmType">
              Вид мероприятия<span className={styles.redStar}>*</span>
            </label>
          </div>
          <SelectInputField
            id="pmType"
            name="pmType"
            options={pmTypesSelectOptions}
            disabled={isWatchOnly}
            size="medium"
            onChange={handlePmTypeChange}
            onOpen={getPmTypeOptions}
            className={styles.width_75}
            opensOnTop={true}
          />
          <div className={styles.tooltipContainer}>
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующих полях:
                  <ul>
                    <p>Контрольный (надзорный) орган</p>
                    <p>Вид контроля (надзора)</p>
                    <p>Основание проведения мероприятия</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button
          text="Сохранить"
          id="saveButton"
          size="medium"
          disabled={isWatchOnly}
          onClick={onClickSaveButton}
          color="primary"
        />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
      <CheckNotificationModal
        notificationType={CheckNotificationTypesEnum.FOR_CREATE_KNM_WITH_GENERAL_PROSECUTOR}
        visible={isNotificationModalVisible}
        onConfirm={onClickButtonApproveNotification}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ICreatePmModalStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isOmbudsman: getUserRoleIsOmbudsman(state),
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  supervisionTypesSelectOptions: getKnmErknmSupervisionTypesSelectOptions(state),
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  userKnoOrganisations: getAccountOrganizationsWithDistrictData(state).kno,
  pmTypesSelectOptions: getKnmErknmProfKindsSelectOptions(state),
  excludeDates: getKnmExcludeDates(state),
});

export const CreatePmModal = connect(mapStateToProps)(CreatePmModalWithoutStore);
